import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch,
    Stack,
} from '@mui/material';
import { Add, Home as HomeIcon, Visibility } from '@mui/icons-material';
import { atualizaArea, deletaArea, listaAreas } from './configs/functions';
import { BlueprintIcon, EditIcon, TrashIcon } from '../../../common/Icons';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { AreaForm } from './AreaForm';
import { useAppContext } from '../../../../contexts/AppContext';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { listaEmpresas } from '../../management/companies/configs/functions';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import { CustomToolbarExportTable } from '../../../common/CustomToolbarExportTable';
import {useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsTableFilter} from "./components/ItemsTableFilter";

const AreaRow = ({ data, actionsAllowed, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);

    async function handleToggleStatus() {
        if (!hasPermission(["admin", "admin_areas", "update_areas"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaArea(data.id, { status: !active })
            if (res) {
                const message = active ? 'Área inativada com sucesso' : 'Área ativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.id, !data.status)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
        }
    }

    const deleteArea = () => {
        const id = data.id;
        deletaArea(id)
            .then((response) => {
                const message = response.data.message ?? 'Área inativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
            }).catch((error) => {
                const message = error.response.data.message ?? 'Erro ao executar operação'
                handleExibirAlerta('Erro', message, 'error');
            }).finally(() => {
                if (updateData) updateData();
        })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                }}
                key={data.id}
            >
                <TableCell sx={{ flex: 2, border: 0 }}>{data.nome}</TableCell>
                <TableCell sx={{ flex: 1, border: 0 }}>{data.codExterno}</TableCell>
                <TableCell sx={{ flex: 3, border: 0 }}>{data.descricao}</TableCell>

                <TableCell sx={{ flex: 1, border: 0 }}>
                    <Switch
                        checked={!!active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_areas", "update_areas"]) || !actionsAllowed}
                    />
                </TableCell>

                <TableCell align='right' sx={{ flex: 1, border: 0, minWidth: 100 }}>
                    {hasPermission(["admin", "admin_areas", "update_areas"]) &&
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            {actionsAllowed ? <EditIcon /> : <Visibility color='primary' />}
                        </IconButton>
                    }

                    {hasPermission(["admin", "admin_areas", "delete_areas"]) && actionsAllowed &&
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>
                    }
                </TableCell>
            </TableRow>

            <ConfirmDialog
                description={'Tem certeza que deseja excluir esta área?'}
                title={'Excluir Área'}
                goAction={deleteArea}
                handleClose={setOpenDialog}
                state={openDialog}
            />
        </>
    )
}

function Areas() {
    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();
    const { verifyIntegrationMode, getEmpresasFilteredByModule } = useModuleContext();

    // estados para controle de paginacao
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);

    const [areas, setAreas] = useState([]);
    const [openForm, setOpenForm] = useState(false)
    const [selectedArea, setSelectedArea] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);

    const [moduleActionsAllowed, setModuleActionsAllowed] = useState(null);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');


    const columns = useMemo(() => [
        { field: 'nome', headerName: 'Área' },
        { field: 'codExterno', headerName: 'Cód. Externo' },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'observacao', headerName: 'Observação' },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status === 1 ? 'Ativo' : 'Inativo' },
    ], []);

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
        nome: searchParams.get('nome'),
        cod_externo: searchParams.get('cod_externo'),
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKeyListAreas = ['listOfAreas', queryFilters];
    const { data: areasData, isLoading, error: areasError, refetch } = useQuery({
        queryKey: queryKeyListAreas,
        queryFn: () => listaAreas(queryFilters),
        enabled: !!searchParamPage && !! searchParamPerPage
    })

    function handleEditArea(item) {
        setSelectedArea(item);
        setOpenForm(true);
    }

    const updateAreaStatus = (areaId, status) => setAreas(
        areas.map((item) => {
            if (item.id === areaId) {
                item.status = status;
            }
            return item;
        })
    );

    useEffect(() => {
        if (listaEmpresasError) {
            exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
        }

        if (areasError) {
            exibirAlerta('Erro', 'Erro ao carregar áreas', 'error');
        }
    }, [exibirAlerta, listaEmpresasError, areasError])

    useEffect(() => {
        if (areasData) {
            setAreas(areasData.data.data);
            setNumTotalItems(areasData.data.numero_total);
            setSelectedEmpresaId(searchParams.get('id_empresa'));
            setEmpresas(empresasData.data.data)
        }
    }, [areasData]);

    useEffect(() => {
        if (selectedEmpresaId) {
            const empresaHasActionsAllowed = verifyIntegrationMode("Area", selectedEmpresaId);
            setModuleActionsAllowed(empresaHasActionsAllowed);
        }
    }, [selectedEmpresaId, verifyIntegrationMode]);

    useEffect(() => {
        if (openForm) return;
        setSelectedArea(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_areas", "list_areas"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    return (<>
        <Helmet title="Áreas" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to="/"><HomeIcon fontSize="small" /></LinkNavegacao >
                    <Typography variant='span'>Gerenciamento</Typography>
                    <LinkNavegacao to="/gerenciamento/organizacao">Organização</LinkNavegacao>
                    <Typography variant='span'>Áreas</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<BlueprintIcon fontSize='large' />}
                    title={'Áreas'}
                    description='Administre as áreas disponíveis das suas empresas.'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    {hasPermission(["admin", "admin_areas", "create_areas"]) &&
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                            >
                                Cadastrar nova área
                            </Button>
                        </Typography>
                    }
                </Stack>
            </Grid>

            <ItemsTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
                item={"Área"}
            />

            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <CustomToolbarExportTable
                        rows={areas}
                        columns={columns}
                        reportName="relatorio_areas"
                        reportPdfTitle="RELATÓRIO DE ÁREAS"
                        getDataWithoutPaginate={() => listaAreas({ id_empresas: [selectedEmpresaId] })}
                        getSelectedEmpresa={() => empresas.find(empresa => empresa.id === Number(selectedEmpresaId))}
                    />

                    <Table aria-label="Áreas">
                        <TableHead>
                            <TableRow sx={{ display: 'flex' }}>
                                <TableCell sx={{ flex: 2 }}>Área</TableCell>
                                <TableCell sx={{ flex: 1 }}>Cód. Externo</TableCell>
                                <TableCell sx={{ flex: 3 }}>Descrição</TableCell>
                                <TableCell sx={{ flex: 1 }}>Status</TableCell>
                                <TableCell sx={{ flex: 1, minWidth: 110 }} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? <LoadingTableRow />
                                : areas && areas?.length > 0
                                    ? areas.map((item) =>
                                        <AreaRow
                                            key={item.id}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditArea}
                                            handleUpdateStatus={updateAreaStatus}
                                            updateData={refetch}
                                            actionsAllowed={moduleActionsAllowed}
                                        />
                                    )
                                    : <EmptyTableRow />
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </TableContainer>
            </Grid>

        </Grid>

        <AreaForm
            open={openForm}
            setOpen={setOpenForm}
            selectedArea={selectedArea}
            empresas={getEmpresasFilteredByModule('Area', empresas, !selectedArea)}
            actionsAllowed={moduleActionsAllowed}
            updateData={refetch}
        />
    </>);
}

export default Areas;